<template>
  <section class="card pd-10px" :style="cardStyle">
    <header class="mgb-1 columns pd-2">
      <div class="column pd-0 is-1">
        <b-icon class="drag draggable-handle mgt-1" icon="drag"/>
      </div>
      <b-checkbox v-model="demande.selected" class="column pd-0 is-1"/>
      <div
        class="has-text-weight-bold is-cursor-pointer column pd-0 is-10 mgt-1"
        @click="$emit('click')">
          <span>{{ demande.title }}</span>
          <qd-icon class="is-pulled-right mgr-2" type="is-primary" icon="information" size="small"/>
      </div>
    </header>
    <div class="mgb-1">
      <span>{{ demande.theme }}</span>
    </div>
    <tag-list class="mgb-0"
            v-if="tags.length > 0"
            :listes="tags"/>
    <div class="mgb-1">
      <router-link-blank
        v-if="demande.annonceurs && demande.commandeId"
        :message="demande.annonceurs"
        :to="{ name: 'c.bodac.commandes', params: { id: demande.commandeId }}"
        :class="{'text-access-no-permission': !checkPermissions(perms.COMMANDES.lecture)}"
        class="lien mgb-1"
      />
      <p class="has-text-default ellipsis-four-line mgb-1"> {{ demande.sujet }} </p>
    </div>
    <img v-if="demande.campagneId" :src="url" class="max-height-80" alt="Logo">
    <footer class="columns is-align-items-center pd-0 mg-0 mgt-10px min-height-33px">
      <div class="column is-4 pd-0">
        <b-tag
          v-if="demande.dateLimite"
          class="pd-2"
          :type="getColorForDate()"
          size="is-large">
            <qd-icon
              size="is-small"
              icon="clock-time-eight"
              class="mg-0">
            </qd-icon>
            <span
              class="subtitle is-6 pdl-5px">
              {{ formatDate(demande.dateLimite || new Date()) }}
            </span>
        </b-tag>
      </div>
      <div class="column is-2 pd-0">
        <div v-if="demande.nbMessage !== 0" class="is-flex is-align-items-center">
          <qd-icon icon="chat-outline"></qd-icon>
          <span class="subtitle is-6 mgl-1">
            {{ demande.nbMessage }}
          </span>
        </div>
      </div>
      <div class="column is-2 pd-0">
        <qd-icon
          v-if="demande.description"
          class="pdt-2"
          icon="text-long">
        </qd-icon>
      </div>
      <div class="column is-1 pd-0">
        <router-link-blank
          v-if="demande.otTechniqueId"
          :to="{ name: 'c.logistique.ordre_travail-tech', params: { id: demande.otTechniqueId }}"
          message="OT"
        />
      </div>
      <div class="column is-3 pd-0 pdr-2">
        <div class="columns is-justify-content-end">
          <div class="column is-narrow pd-0 mgr-1">
            <b-tooltip
              v-if="assistantName"
              :label="$t('suiviProduction.card.detailCard.assistant')"
              position="is-left">
              <avatar
                :username="assistantName"
                :size="33"/>
            </b-tooltip>
          </div>
          <div class="column is-narrow pd-0">
            <b-tooltip
              v-if="commercialName"
              :label="$t('suiviProduction.card.detailCard.commercial')"
              position="is-left">
              <avatar
                :username="commercialName"
                :size="33"/>
            </b-tooltip>
          </div>
        </div>
      </div>
    </footer>
  </section>
</template>

<script>
import moment from 'moment';
import Avatar from 'vue-avatar';
import { createNamespacedHelpers } from 'vuex';
import { COMMANDES } from '@/configuration/permissionsBodacConf';
import RouterLinkBlank from '@/components/general/routerLinkBlank';
import { PARAMETRES_KEYS } from '@/store/modules/parametres-mutation-type';
import messageErreur from '@/services/general/messageErreur';
import nomPrenomGetter from '@/services/general/nomPrenomGetter';
import tagList from '@/components/general/tagList';

const Parametres = createNamespacedHelpers('parametres');
const Permissions = createNamespacedHelpers('permissions');

export default {
  name: 'suiviProductionCard',
  props: {
    demande: {
      type: Object,
      required: true,
    },
  },
  components: {
    Avatar,
    RouterLinkBlank,
    tagList,
  },
  data() {
    return {
      countMsg: null,
      attachement: null,
      perms: {
        COMMANDES,
      },
    };
  },
  computed: {
    ...Permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    ...Parametres.mapGetters({
      parametre: 'parametre',
    }),
    url() {
      return this.demande.urlLogoCampagne || '/static/img/default-placeholder.png';
    },
    commercialName() {
      const userFound = this.parametre(PARAMETRES_KEYS.utilisateur)
        .find(u => u.id === this.demande.commercialId);
      if (!userFound) return '';
      return nomPrenomGetter(userFound);
    },
    assistantName() {
      const userFound = this.parametre(PARAMETRES_KEYS.utilisateur)
        .find(u => u.id === this.demande.assistantId);
      if (!userFound) return '';
      return nomPrenomGetter(userFound);
    },
    tags() {
      if (!this.demande?.tags?.length) return [];
      return this.demande.tags.map(t => t.tag);
    },
    cardStyle() {
      return this.demande.color ? `background-color: ${this.demande.color}` : '';
    },
  },
  methods: {
    ...Parametres.mapActions({
      loadParametreUtilisateur: 'loadParametreUtilisateur',
    }),
    formatDate(date) {
      return moment(date).format('DD MMM');
    },
    getColorForDate() {
      const today = moment();
      const diffInDays = moment(this.demande.dateLimite).diff(today, 'days', true);
      if (diffInDays < 0) return 'is-danger is-light';
      if (diffInDays <= 7) return 'is-warning is-light';
      return 'is-success is-light';
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.loadParametreUtilisateur();
      loadingComponent.close();
    } catch (e) {
      messageErreur(this.$i18n);
      loadingComponent.close();
      throw (e);
    }
  },
};
</script>
